import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { shopApi } from "@/shop/shopApi";
import ProductCard from "@/shop/ProductCard";
import { ShopEmpty, ProductSkeleton } from "@/shop/ShopStates";
import { Reveal } from "@/shop/motion";

const SORTS = [
  { id: "best-selling", label: "Best selling" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
  { id: "title", label: "Alphabetical" },
];

export default function ShopAll() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ loading: true, configured: true, products: [] });
  const [query, setQuery] = useState(params.get("q") || "");
  const sort = params.get("sort") || "best-selling";

  useEffect(() => {
    setData((d) => ({ ...d, loading: true }));
    shopApi.products({ sort, query: params.get("q") || undefined, first: 48 })
      .then((d) => setData({ loading: false, configured: d.configured, products: d.products || [] }))
      .catch(() => setData({ loading: false, configured: false, products: [] }));
  }, [sort, params]);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query) next.set("q", query); else next.delete("q");
    setParams(next);
  };
  const setSort = (s) => { const next = new URLSearchParams(params); next.set("sort", s); setParams(next); };

  return (
    <div className="pt-28 pb-28" data-testid="shop-all-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">Shop</p>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-[-0.03em]">All products</h1>
        </Reveal>

        <div className="mt-12 flex flex-col md:flex-row gap-4 md:items-center justify-between sticky top-16 z-30 bg-black/60 backdrop-blur-xl py-4 -mx-2 px-2 rounded-2xl">
          <form onSubmit={submitSearch} className="relative flex-1 max-w-md" data-testid="shop-search-form">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products…" className="w-full h-12 pl-11 pr-4 rounded-full bg-white/5 border border-white/15 focus:border-sh-blue outline-none text-white placeholder:text-white/30" data-testid="shop-search-input" />
          </form>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <SlidersHorizontal className="w-4 h-4 text-white/40 shrink-0" />
            {SORTS.map((s) => (
              <button key={s.id} onClick={() => setSort(s.id)} data-testid={`sort-${s.id}`} className={`whitespace-nowrap px-4 py-2 rounded-full text-[12px] border transition-colors ${sort === s.id ? "border-sh-blue bg-sh-blue text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          {data.loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">{Array.from({length: 8}).map((_, i) => <ProductSkeleton key={i} />)}</div>
          ) : data.products.length === 0 ? (
            <ShopEmpty title={data.configured ? "No products found" : "Store launching soon"} note={data.configured ? "Try a different search or sort." : "Your Shopify catalog will appear here once connected."} />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="shop-product-grid">
              {data.products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
